import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  GlobalMessageService,
  GlobalMessageType,
  TranslationService
} from '@spartacus/core';
import { on } from 'events';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import {
  CustomOrderHistory, CustomOrderHistoryDefaultSortOptions,
  CustomOrderHistoryEntry, CustomOrderHistoryFilter,
  CustomOrderHistoryOverview, CustomOrderHistoryRemainsSortOptions,
  CustomOrderHistoryFilterAndSortData
} from '../../custom-order-history.models';
import { CustomOrderHistoryService } from '../../custom-order-history.service';
import {
  cancelOrder,
  cancelOrderReset,
  clearOrderHistoryOverview,
  getOrderHistoryOverview,
  resetBackToOrderHistory,
  sortOrderHistory
} from '../../store/custom-order-history.actions';
import {
  errorCancelOrder,
  errorOverview,
  loadingOverview,
  selectBackToOverview,
  selectOverview,
  selectOverviewFilters,
  successCancelOrder
} from '../../store/custom-order-history.selectors';

@Component({
    selector: 'app-custom-order-history-overview',
    templateUrl: './custom-order-history-overview.component.html',
    styleUrls: [
        './custom-order-history-overview.component.scss',
        './custom-order-history-overview-print.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CustomOrderHistoryOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('f') searchForm: NgForm;

  filterOptions = Object.values(CustomOrderHistoryFilter).filter(
    (value) => typeof value === 'string'
  ) as string[];

  showDefault: boolean = true;
  selectedFilterOption: string =
    CustomOrderHistoryFilter[CustomOrderHistoryFilter.OPEN];
  uigOrderNumber: string = '';
  searchFor: string = '';
  productCode: string = '';
  orderDateFrom: string;
  orderDateFromMin: string;
  orderDateFromMax: Date = new Date();
  orderDateTo: string;
  orderDateToMin: string;
  orderDateToMax: Date = new Date();
  orderHistoryFilters: CustomOrderHistoryFilterAndSortData;
  backToOverview: boolean;

  selectedSortOption: string;
  sortOptions: string[];
  sortDirection: string = 'desc';
  compare = (v1: string | number | Date, v2: string | number | Date) => {
    let c1 = typeof v1 === "string" ? v1.toString().toLocaleLowerCase() : v1;
    let c2 = typeof v2 === "string" ? v2.toString().toLocaleLowerCase() : v2;
    return c1 < c2 ? -1 : c1 > c2 ? 1 : 0;
  }

  overview$: Observable<CustomOrderHistoryOverview> = this.store.select(
    selectOverview
  );
  overview: CustomOrderHistoryOverview;
  orders: CustomOrderHistory[];
  orderEntries: CustomOrderHistoryEntry[];
  isLoadingOverview$: Observable<boolean> = this.store.select(loadingOverview);
  errorOverview$: Observable<Error> = this.store.select(errorOverview);
  errorOverview: Error;
  orderHistoryFilters$: Observable<CustomOrderHistoryFilterAndSortData> = this.store.select(selectOverviewFilters);
  backToOverview$: Observable<boolean> = this.store.select(selectBackToOverview);

  isSuccessCancelOrder$: Observable<boolean> = this.store.select(
    successCancelOrder
  );
  errorCancelOrder$: Observable<boolean> = this.store.select(errorCancelOrder);

  constructor(
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private modalService: NgbModal,
    private customOrderHistoryService: CustomOrderHistoryService,
    private store: Store<{ overview: CustomOrderHistoryOverview }>
  ) { }

  ngOnInit() {
    if (!this.backToOverview) {
      this.setSortOptions();
    }
    this.handleSavedFiltersAndSorting();
    this.fetchOrders();
    this.setShowDefault();
    this.overview$
      .pipe(
        filter((overview) => overview != null),
        tap((overview) => {
          this.overview = overview;
          this.orders = [...overview.orders];
          this.onChangeSort();
        })
      )
      .subscribe();
    this.errorOverview$
      .pipe(
        filter((error) => error != null),
        tap((error) => {
          this.errorOverview = error;
        }),
        switchMap(() => this.translation.translate('customOrderHistory.error')),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
        })
      )
      .subscribe();
    this.errorCancelOrder$
      .pipe(
        filter((error) => error),
        switchMap(() =>
          this.translation.translate('customOrderHistory.errorCancelOrder')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
          this.store.dispatch(cancelOrderReset());
        })
      )
      .subscribe();
    this.isSuccessCancelOrder$
      .pipe(
        filter((success) => success),
        switchMap(() =>
          this.translation.translate('customOrderHistory.successCancelOrder')
        ),
        tap((text) => {
          this.messageService.add(
            text,
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
          this.store.dispatch(cancelOrderReset());
          this.onSubmit();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearOrderHistoryOverview());
  }

  setSortOptions() {
    if (this.showDefault) {
      if (this.backToOverview) {
        this.selectedSortOption = this.orderHistoryFilters.sortOption;
      } else {
        this.selectedSortOption =
          CustomOrderHistoryDefaultSortOptions[
          CustomOrderHistoryDefaultSortOptions.code
          ];
      }
      this.sortOptions = Object.values(
        CustomOrderHistoryDefaultSortOptions
      ).filter((value) => typeof value === 'string') as string[];
    } else {
      if (this.backToOverview) {
        this.selectedSortOption = this.orderHistoryFilters.sortOption;
      } else {
        this.selectedSortOption =
          CustomOrderHistoryRemainsSortOptions[
          CustomOrderHistoryRemainsSortOptions.code
          ];
      }
      this.sortOptions = Object.values(
        CustomOrderHistoryRemainsSortOptions
      ).filter((value) => typeof value === 'string') as string[];
    }
  }

  onChangeSort() {
    if (this.showDefault) {
      this.orders = [...this.overview.orders].sort((a, b) => {
        const res = this.compare(
          a[this.selectedSortOption],
          b[this.selectedSortOption]
        );
        return this.sortDirection === 'asc' ? res : -res;
      });
    } else {
      if (this.overview.orders.length > 0) {
        this.orderEntries = [...this.overview.orders[0].entries].filter(entry => {
          if (!this.searchFor) {
            return true;
          }
          let search = this.searchFor.replace(/\s/g, "").toLocaleLowerCase();
          let result = true;
          if (search !== "") {
            result = false;
            if (entry.code) {
              result = result || entry.code.toString().toLocaleLowerCase().indexOf(search) > -1;
            }
            if (entry.productCode) {
              result = result || ("" + entry.productCode).indexOf(search) > -1;
            }
            if (entry.description) {
              result = result || entry.description.toLocaleLowerCase().indexOf(search) > -1;
            }
          }
          return result
        });
        this.orderEntries.sort((a, b) => {
          const res = this.compare(
            a[this.selectedSortOption],
            b[this.selectedSortOption]
          );
          return this.sortDirection === 'asc' ? res : -res;
        });
      }
    }
    this.store.dispatch(sortOrderHistory({
      customOrderHistorySortData: this.getRequestData(true)
    }
    ));
  }

  onChangeFilter() {
    this.setShowDefault();
    this.setSortOptions();
    this.sortDirection = 'desc';
    this.fetchOrders();
  }

  onSubmit() {
    if (this.showDefault) {
      this.uigOrderNumber = this.searchForm.value.uigOrderNumber;
      this.productCode = this.searchForm.value.productCode;
      this.orderDateFrom = this.searchForm.value.orderDateFrom;
      this.orderDateTo = this.searchForm.value.orderDateTo;
      this.fetchOrders();
    }
    else {
      this.onChangeSort();
    }
  }

  onReset() {
    this.uigOrderNumber = "";
    this.productCode = "";
    this.orderDateFrom = "";
    this.orderDateTo = "";
    this.searchFor = "";
    this.onSubmit();
  }

  onCancelOrder(event: any, confirmationMessage) {
    this.modalService.open(confirmationMessage, { centered: true }).result.then(
      () => {
        this.store.dispatch(
          cancelOrder({
            uigOrderNumber: event,
          })
        );
      },
      () => { }
    );
  }

  onExport() {
    this.customOrderHistoryService.printOrExportOrderOverview(this.getRequestData(false));
  }

  getRequestData(sortingOnly: boolean): CustomOrderHistoryFilterAndSortData {
    var requestData = {} as CustomOrderHistoryFilterAndSortData;
    requestData.type = this.selectedFilterOption;
    if (!sortingOnly) {
      requestData.orderId = this.uigOrderNumber;
      requestData.productCode = this.productCode;
      requestData.orderDateFrom = this.orderDateFrom;
      requestData.orderDateTo = this.orderDateTo;
    }
    else {
      requestData.orderId = this.orderHistoryFilters.orderId;
      requestData.productCode = this.orderHistoryFilters.productCode;
      requestData.orderDateFrom = this.orderHistoryFilters.orderDateFrom;
      requestData.orderDateTo = this.orderHistoryFilters.orderDateTo;
    }
    requestData.sortDirection = this.sortDirection;
    requestData.sortOption = this.selectedSortOption;
    return requestData;
  }

  fetchOrders(): void {
    this.overview = null;
    this.orders = null;
    this.orderEntries = null;
    this.errorOverview = null;
    this.store.dispatch(getOrderHistoryOverview({
      formData: this.getRequestData(false)
    }));
  }

  changeOrderDateFrom(event: any) {
    this.orderDateFrom = event.target.value;
    this.orderDateToMin = event.target.value;
    if (new Date(this.orderDateFrom) > new Date(this.orderDateTo)) {
      this.orderDateTo = this.orderDateFrom;
    }
  }

  changeOrderDateTo(event: any) {
    this.orderDateTo = event.target.value;
  }

  handleSavedFiltersAndSorting() {
    this.backToOverview$
      .pipe(
        filter((backToOverview) => backToOverview != null),
        tap((backToOverview) => {
          this.backToOverview = backToOverview;
        })
      )
      .subscribe();

    this.orderHistoryFilters$
      .pipe(
        filter((orderHistoryFilters) => orderHistoryFilters != null),
        tap((orderHistoryFilters) => {
          this.orderHistoryFilters = orderHistoryFilters;
        })
      )
      .subscribe();

    if (this.backToOverview) {
      this.usePresetFilters();
      this.setSortOptions();
      this.store.dispatch(resetBackToOrderHistory());
    }
  }

  usePresetFilters() {
    if (this.orderHistoryFilters) {
      this.selectedFilterOption = this.orderHistoryFilters.type;
      this.uigOrderNumber = this.orderHistoryFilters.orderId;
      this.productCode = this.orderHistoryFilters.productCode;
      this.orderDateFrom = this.orderHistoryFilters.orderDateFrom;
      this.orderDateTo = this.orderHistoryFilters.orderDateTo;
      this.sortDirection = this.orderHistoryFilters.sortDirection;
      this.selectedSortOption = this.orderHistoryFilters.sortOption;
      this.setShowDefault();
    }
  }

  setShowDefault() {
    if (this.backToOverview && this.orderHistoryFilters && this.orderHistoryFilters.type) {
      this.showDefault = this.orderHistoryFilters.type !== 'REMAINS';
    } else {
      this.showDefault = this.selectedFilterOption !==
        CustomOrderHistoryFilter[CustomOrderHistoryFilter.REMAINS];
    }
  }
}
